// The reader's HTML of a long text (decision 034): the same blocks the editor shows, for the admin's preview, the public site and
// letters. Headings start at <h2>: the page's <h1> is the record's title.

import { isImage, parseBlocks, sameList, type Block } from './blocks.ts';
import { escHtml, inlineToHtml, type LinkPolicy } from './inline.ts';

/** Files of the admin (`/api/files/…`) open only for the team; `file` decides where such an address leads (null — not shown). */
export interface RenderPolicy extends LinkPolicy { file?: (url: string) => string | null }

function fileHref(url: string, policy: RenderPolicy): string | null {
  if (/^\/api\/files\//.test(url)) return policy.file ? policy.file(url) : url;
  return /^https?:\/\//i.test(url) ? url : null;
}

function blockHtml(b: Block, policy: RenderPolicy): string {
  const inl = (s: string) => inlineToHtml(s, policy);
  switch (b.kind) {
    case 'p': return `<p>${inl(b.text)}</p>`;
    case 'h1': return `<h2>${inl(b.text)}</h2>`;
    case 'h2': return `<h3>${inl(b.text)}</h3>`;
    case 'h3': return `<h4>${inl(b.text)}</h4>`;
    case 'bullet': case 'number': return `<li>${inl(b.text)}</li>`;
    case 'todo': return `<li class="todo${b.checked ? ' done' : ''}"><input type="checkbox" disabled${b.checked ? ' checked' : ''}> ${inl(b.text)}</li>`;
    case 'quote': return `<blockquote>${inl(b.text)}</blockquote>`;
    case 'callout': return `<aside class="callout">${inl(b.text)}</aside>`;
    case 'code': return `<pre><code>${escHtml(b.text)}</code></pre>`;
    case 'divider': return '<hr>';
    case 'file': {
      const href = fileHref(b.url ?? '', policy);
      if (!href) return '';
      return isImage(b) ? `<figure><img src="${escHtml(href)}" alt="${escHtml(b.text)}" loading="lazy"></figure>` : `<p><a class="file" href="${escHtml(href)}">${escHtml(b.text || 'Файл')}</a></p>`;
    }
    case 'record': {
      const href = policy.record ? policy.record(b.recordId!) : `/r/${b.recordId}`;
      return href ? `<p><a href="${escHtml(href)}" data-record="${escHtml(b.recordId!)}">${escHtml(b.text)}</a></p>` : `<p>${escHtml(b.text)}</p>`;
    }
  }
}

export function renderBlocks(blocks: Block[], policy: RenderPolicy = {}): string {
  const out: string[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    if (b.kind === 'p' && !b.text.trim()) continue;
    if (b.kind === 'bullet' || b.kind === 'todo' || b.kind === 'number') {
      const items: string[] = [];
      for (; i < blocks.length && (blocks[i] === b || sameList(blocks[i - 1]!, blocks[i]!)); i++) items.push(blockHtml(blocks[i]!, policy));
      i--;
      out.push(b.kind === 'number' ? `<ol>${items.join('')}</ol>` : `<ul>${items.join('')}</ul>`);
      continue;
    }
    out.push(blockHtml(b, policy));
  }
  return out.join('\n');
}

/** Markdown of a long text to the reader's HTML. */
export const markdownToHtml = (src: string, policy: RenderPolicy = {}) => renderBlocks(parseBlocks(src), policy);

/** Where a text on the public site leads: the account's record links and files are not the visitor's — their titles stay. */
export const PUBLIC_TEXT: RenderPolicy = { record: () => null, file: () => null };

/** A block of a short text as inline HTML: a code as text, a record link as its title, a file not shown. */
const inlineOf = (b: Block, policy: RenderPolicy) => (b.kind === 'code' || b.kind === 'record' ? escHtml(b.text) : inlineToHtml(b.text, policy));
const shortBlocks = (src: string) => parseBlocks(src).filter((b) => b.text.trim() && b.kind !== 'file');

const isItem = (b: Block) => b.kind === 'bullet' || b.kind === 'number' || b.kind === 'todo';

/**
 * A short text in one place (a subtitle, a note under a form): a paragraph of the place's class per block, marks and links kept;
 * list items stay a list of the same class — their markers are the text's (MiniMax review P2: a plain «- пункт» lost its dash).
 */
export function markdownParagraphs(src: string, cls: string, policy: RenderPolicy = PUBLIC_TEXT): string {
  const c = escHtml(cls);
  const blocks = shortBlocks(src);
  const out: string[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!;
    if (!isItem(b)) { out.push(`<p class="${c}">${inlineOf(b, policy)}</p>`); continue; }
    const items: string[] = [];
    for (; i < blocks.length && (blocks[i] === b || sameList(blocks[i - 1]!, blocks[i]!)); i++) items.push(`<li>${inlineOf(blocks[i]!, policy)}</li>`);
    i--;
    out.push(b.kind === 'number' ? `<ol class="${c}">${items.join('')}</ol>` : `<ul class="${c}">${items.join('')}</ul>`);
  }
  return out.join('');
}

/** A text inside a line of a card (the description of an offer): the blocks' text with marks, lines apart, list items with their marker. */
export function markdownInline(src: string, policy: RenderPolicy = PUBLIC_TEXT): string {
  let n = 0;
  let prev: Block | null = null;
  return shortBlocks(src).map((b) => {
    n = b.kind === 'number' && prev?.kind === 'number' ? n + 1 : 1;
    prev = b;
    const mark = b.kind === 'bullet' ? '• ' : b.kind === 'number' ? `${n}. ` : b.kind === 'todo' ? (b.checked ? '☑ ' : '☐ ') : '';
    return mark + inlineOf(b, policy);
  }).join('<br>');
}
